import SearchBar from "@/ui-components/search-bar";
import { useSearchFirehose } from "./hooks/useSearchFirehose";
import { FirehoseHighlightItem } from "./components/FirehoseHighlightItem";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";
import { trackSearchSubmit, trackSearchResults } from "@/utils/gtm";
import { BackendSource } from "@/shared/api/firehose";
import { getDisplayTime } from "@/shared/api/firehose/models";
import { CommonSearchProps } from "@/widget-components/newsquawk-headlines/HeadlinesSearchForm";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import {
  VirtuosoWrapper,
  GroupingUtils,
} from "@/ui-components/grouped-virtuoso";
import { DateSectionHeader } from "../shared/DateSectionHeader";

export interface FirehoseSearchProps extends CommonSearchProps {
  source?: BackendSource[];
  defaultTitle?: string;
}

function FirehoseSearchForm(props: FirehoseSearchProps) {
  const {
    isLoading,
    isError,
    searchFirehose,
    searchResults,
    clearSearchResults,
    loadNextPage,
    hasMore,
  } = useSearchFirehose();

  const { setTitle } = props;

  const [query, setQuery] = useState<string>("");

  const [startDate, setStartDate] = React.useState<string>("");
  const [startTime, setStartTime] = React.useState<string>("00:00");
  const [endDate, setEndDate] = React.useState<string>("");
  const [endTime, setEndTime] = React.useState<string>("23:59");

  const formRef = React.useRef<HTMLDivElement>(null);

  const [formHeight, setFormHeight] = React.useState<number>(40);

  const showSearchResults = searchResults && searchResults.length > 0;

  const handleSearch = (query: string) => {
    clearSearchResults();

    setQuery(query);

    // Convert date strings to ISO strings with time
    let startDateISO: string | undefined;
    let endDateISO: string | undefined;

    if (startDate) {
      const [h, m] = (startTime || "00:00").split(":").map(Number);
      const startDateObj = new Date(startDate);
      startDateObj.setHours(h, m, 0, 0);
      startDateISO = startDateObj.toISOString();
    }

    if (endDate) {
      const [h, m] = (endTime || "23:59").split(":").map(Number);
      const endDateObj = new Date(endDate);
      endDateObj.setHours(h, m, 59, 999);
      endDateISO = endDateObj.toISOString();
    }

    searchFirehose({
      q: query,
      source: props.source,
      limit: 50,
      date_range: { start_date: startDateISO, end_date: endDateISO },
    });

    const activeFilters: string[] = [];
    if (startDate || endDate) activeFilters.push("date_range");
    if (props.source?.length)
      activeFilters.push(`sources:${props.source.join(",")}`);
    trackSearchSubmit({
      search_term: query,
      search_context: "firehose",
      search_filters:
        activeFilters.length > 0 ? activeFilters.join(", ") : undefined,
    });
  };

  const handleClear = () => {
    setQuery("");
    setStartDate("");
    setEndDate("");
    clearSearchResults();
  };

  useEffect(() => {
    if (!isError) return;
    toast.error(
      "There was an error fetching the firehose stories, please try again."
    );
    Sentry.captureMessage("Error fetching firehose stories in search");
  }, [isError]);

  useEffect(() => {
    if (searchResults && searchResults.length > 0 && query) {
      const activeFilters: string[] = [];
      if (startDate || endDate) activeFilters.push("date_range");
      trackSearchResults({
        search_term: query,
        search_context: "firehose",
        results_count: searchResults.length,
        search_filters:
          activeFilters.length > 0 ? activeFilters.join(", ") : undefined,
      });
    }
  }, [searchResults?.length, query, startDate, endDate]);

  useEffect(() => {
    const titleString = `Showing results for '${query}'`;

    if (showSearchResults) {
      setTitle?.(titleString);
    } else {
      setTitle?.(props.defaultTitle || "External Firehose");
    }

    return () => setTitle?.(props.defaultTitle || "External Firehose");
  }, [showSearchResults, query, setTitle, props.defaultTitle]);

  // Clear search results when the component unmounts
  useEffect(() => clearSearchResults, [clearSearchResults]);

  // Measure and update form height dynamically
  useEffect(() => {
    const updateFormHeight = () => {
      if (formRef.current) {
        const height = formRef.current.offsetHeight;
        setFormHeight(height);
      }
    };

    // Initial measurement
    updateFormHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(updateFormHeight);
    if (formRef.current) {
      resizeObserver.observe(formRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <React.Fragment>
      <div
        className="bg-gray-800 border-b border-gray-700/50 px-3 py-2 space-y-2"
        ref={formRef}
      >
        {/* Search bar — auto-submits as you type; Done (below) is the only panel-close. */}
        <SearchBar
          loading={isLoading}
          onSearch={handleSearch}
          onClear={handleClear}
        />

        {/* Date/time range. Sticky so Done stays reachable on a short widget. */}
        <div className="sticky bottom-0 z-10 bg-gray-800 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          {props.onClose && (
            <button
              onClick={props.onClose}
              className="ml-auto px-3 py-1 text-xs font-medium rounded bg-squawk-blue hover:bg-squawk-blue/80 text-white transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>

      {showSearchResults ? (
        <section
          className="absolute flex-1 z-30 bg-white dark:bg-gray-900 inset-0 pb-[40px]"
          style={{ top: `${formHeight}px` }}
        >
          <VirtuosoWrapper
            data={searchResults ?? []}
            grouping={{
              groupBy: (items) =>
                GroupingUtils.groupByDate(
                  items,
                  (item) => getDisplayTime(item),
                  {
                    includeYesterday: true,
                  }
                ),
              hideFirstGroupHeader: false,
            }}
            scrollManager={{
              enabled: false,
            }}
            pagination={{
              hasMore,
              isLoadingMore: isLoading,
              loadNext: loadNextPage,
            }}
            renderItem={(item) => <FirehoseHighlightItem item={item} />}
            renderGroupHeader={(title) => <DateSectionHeader title={title} />}
            className="scrollbar-thin absolute inset-0"
          />
        </section>
      ) : query && !isLoading ? (
        <section
          className="absolute flex-1 z-30 bg-white dark:bg-gray-900 inset-0 pb-[40px]"
          style={{ top: `${formHeight}px` }}
        >
          <EmptyPlaceholder
            message={"No firehose stories found"}
            isLoading={false}
          />
        </section>
      ) : null}
    </React.Fragment>
  );
}

export default FirehoseSearchForm;
